import ComposePlugins, { addComposePlugins } from './ComposePlugins';
import ContentOperationPlugin from './contentOperation/ContentOperationPlugin';
import preloadComposePlugins from '../actions/preloadComposePlugins';
import switchToReadWriteRecipient from '../actions/switchToReadWriteRecipient';
import canModifyRecipients from '../utils/canModifyRecipients';
import createAttachments from '../utils/createAttachments';
import createPluginViewStateAdapter from '../utils/createPluginViewStateAdapter';
import deletePendingInlineAttachment from '../utils/deletePendingInlineAttachment';
import fixInlineImageSrc from '../utils/fixInlineImageSrc';
import { getRecipientWellsFromComposeViewState } from '../utils/getAllRecipientsAsEmailAddressStrings';
import getCurrentFromAddress from '../utils/getFromAddressFromRecipientWell';
import isSmartComposeEnabled from '../utils/isSmartComposeEnabled';
import { AddinEditorPlugin } from 'owa-addins-editor-plugin';
import { AttachmentFile, AttachmentFileList } from 'owa-attachment-file-types';
import { CreateAttachmentsProperties, InlineAttachmentState } from 'owa-attachment-well-data';
import { LazyCortanaPickerPluginClass } from 'owa-cortana-compose';
import { UndoService } from 'owa-editor';
import { LazyAccCheckerPluginClass } from 'owa-editor-acc-checker-plugin';
import { LazyAugLoopPluginClass } from 'owa-editor-augloop-plugin';
import { LazyDictationPluginClass } from 'owa-editor-dictation-plugin';
import { createDockingPlugin } from 'owa-editor-docking-plugin';
import { LazyEmojiPickerPluginClass } from 'owa-editor-emoji-picker-plugin';
import { LazyLinkPluginClass } from 'owa-editor-link-plugin';
import { LazyAtMentionsPickerPluginClass } from 'owa-editor-mentions-picker-plugin';
import { LazyPasteOptionPluginClass } from 'owa-editor-paste-option-plugin';
import { getAnnotationHandlerAsync as getProofingAnnotationHandlerAsync } from 'owa-editor-proofing-handler';
import RibbonPlugin from 'owa-editor-ribbonplugin/lib/plugin/RibbonPlugin';
import { LazySmartSuggestionsPluginClass } from 'owa-editor-smart-suggestions-plugin';
import { LazySmartComposePluginClass } from 'owa-editor-smartcompose-plugin';
import { TableEditPlugin } from 'owa-editor-table-edit-plugin';
import { isFeatureEnabled } from 'owa-feature-flags';
import { LazyLinkPreviewPluginClass } from 'owa-link-preview-compose';
import { ComposeViewState } from 'owa-mail-compose-store';
import { LazyPraguePluginClass } from 'owa-prague-integrations';
import getRecipientsFromRecipientWells from 'owa-recipient-common/lib/utils/getRecipientsFromRecipientWells';
import getUserConfiguration from 'owa-session-store/lib/actions/getUserConfiguration';
import {
    InlineImagePlugin,
    LazyInlineImageResizePluginClass,
    CreateAttachments,
    DeletePendingInlineAttachment,
    lazyGetSelectedImage,
} from 'owa-inline-image';

function getCreateAttachmentsHandler(viewState: ComposeViewState): CreateAttachments {
    return function(
        files: AttachmentFileList,
        isInline: boolean,
        shouldShare: boolean,
        isHiddenInline: boolean,
        processInlineImageBeforeUploadFunc: (temporaryId: string, file: AttachmentFile) => void
    ) {
        const baseProperties = { isInline, isHiddenInline };
        const createAttachmentsProperties: CreateAttachmentsProperties = shouldShare
            ? { ...baseProperties, shouldShare: true }
            : { ...baseProperties, shouldShare: false };
        createAttachments(
            files,
            viewState,
            createAttachmentsProperties,
            processInlineImageBeforeUploadFunc
        );
    };
}

function getDeleteInlineAttachmentsHandler(
    viewState: ComposeViewState
): DeletePendingInlineAttachment {
    return function(placeholderId: string) {
        deletePendingInlineAttachment(placeholderId, viewState);
    };
}

function getAttachmentStatesHandler(viewState: ComposeViewState): () => InlineAttachmentState[] {
    return () => viewState.attachmentWell.inlineAttachments;
}

function getSubjectHandler(viewState: ComposeViewState): () => string {
    return () => viewState.subject;
}

export default function createComposePlugins(
    viewState: ComposeViewState,
    undo: UndoService
): ComposePlugins {
    const dockingPlugin = createDockingPlugin(
        createPluginViewStateAdapter(viewState, viewState => viewState.docking)
    );
    const scrollAdapter = dockingPlugin.getScrollAdapter();

    // PasteManager depends on InlineImagePlugin to attach inline images.
    const inlineImagePlugin = new InlineImagePlugin(
        getAttachmentStatesHandler(viewState),
        getCreateAttachmentsHandler(viewState),
        getDeleteInlineAttachmentsHandler(viewState),
        fixInlineImageSrc(viewState)
    );

    const getAllRecipientsFunc = (viewState: ComposeViewState) => () => {
        return getRecipientsFromRecipientWells(getRecipientWellsFromComposeViewState(viewState));
    };

    const getAllRecipientWellsFunc = (viewState: ComposeViewState) => () => {
        return getRecipientWellsFromComposeViewState(viewState);
    };

    const switchToReadWriteRecipientWell = (viewState: ComposeViewState) => () => {
        if (viewState.showCompactRecipientWell) {
            switchToReadWriteRecipient(viewState);
        }
    };

    const isRtl = getUserConfiguration().SessionSettings.IsUserCultureRightToLeft;
    const composePlugins: ComposePlugins = {
        undo: undo,
        dockingPlugin: dockingPlugin,
        ribbonPlugin: new RibbonPlugin(
            createPluginViewStateAdapter(viewState, viewState => viewState.ribbon),
            lazyGetSelectedImage.import()
        ),
        inlineImagePlugin: inlineImagePlugin,
        inlineImageResizePlugin: new LazyInlineImageResizePluginClass(scrollAdapter),
        mentionsPickerPlugin: new LazyAtMentionsPickerPluginClass(
            createPluginViewStateAdapter(viewState, viewState => viewState.atMentions),
            scrollAdapter,
            viewState.toRecipientWell,
            switchToReadWriteRecipientWell(viewState),
            canModifyRecipients(viewState),
            getAllRecipientsFunc(viewState)
        ),
        cortanaComposePlugin:
            isFeatureEnabled('mc-cortanaCompose') &&
            new LazyCortanaPickerPluginClass(scrollAdapter),
        emojiPickerPlugin: new LazyEmojiPickerPluginClass(
            createPluginViewStateAdapter(viewState, viewState => viewState.emojiPicker),
            scrollAdapter,
            true /* useFlexPane */
        ),
        contentOperationPlugin: new ContentOperationPlugin(viewState),
        smartSuggestionsPlugin: isFeatureEnabled('mc-smartSuggestions')
            ? new LazySmartSuggestionsPluginClass(getAllRecipientWellsFunc(viewState))
            : null,
        linkPreviewPlugin: new LazyLinkPreviewPluginClass(scrollAdapter),
        addinPlugin: isFeatureEnabled('rp-addins') ? new AddinEditorPlugin() : null,
        pasteOptionPlugin: new LazyPasteOptionPluginClass(
            createPluginViewStateAdapter(viewState, viewState => viewState.pasteOption),
            scrollAdapter,
            isRtl
        ),
        tableEdit: new TableEditPlugin(
            createPluginViewStateAdapter(viewState, viewState => viewState.tableEdit)
        ),
        accCheckerPlugin: new LazyAccCheckerPluginClass(
            createPluginViewStateAdapter(viewState, viewState => viewState.accChecker)
        ),
        linkPlugin: !isFeatureEnabled('cmp-prague')
            ? new LazyLinkPluginClass(
                  viewState.attachmentWell,
                  scrollAdapter,
                  getAllRecipientWellsFunc(viewState),
                  getCurrentFromAddress(viewState.toRecipientWell)
              )
            : null,
        smartComposePlugin: isSmartComposeEnabled()
            ? new LazySmartComposePluginClass(
                  getAllRecipientWellsFunc(viewState),
                  getSubjectHandler(viewState),
                  viewState.operation,
                  viewState.composeFeedbackViewState
              )
            : null,
        praguePlugin: isFeatureEnabled('cmp-prague') ? new LazyPraguePluginClass() : null,
        augLoopPlugin: isFeatureEnabled('mc-augmentationLoop')
            ? new LazyAugLoopPluginClass([getProofingAnnotationHandlerAsync])
            : null,
        dictationPlugin: isFeatureEnabled('cmp-dictation')
            ? new LazyDictationPluginClass(viewState)
            : null,
    };

    // Setting the current compose plugins to the newly created one
    addComposePlugins(viewState, composePlugins);
    preloadComposePlugins(viewState);
    return composePlugins;
}
